import React from "react";
import { Chart } from "primereact/chart";
import { MonthlyDuration } from "../../../../types/onboardingAnalytics";
import { COCHARGE_DARK_GREEN } from "../../../../styles/theme_constants";
import {
  formatDays,
  hasMeasurement,
  monthLabel,
} from "../../../../utils/analyticsFormat";

interface DurationTrendChartProps {
  months: MonthlyDuration[];
}

/**
 * Average total onboarding duration per calendar month.
 *
 * A month in which nobody went live comes back as `avg_days: 0.0, count: 0`.
 * Plotting that as zero would draw a line to the floor and read as "onboarding
 * took no time that month", so those months are plotted as gaps (`null`) and
 * listed as having no data (R10). chart.js renders a null as a break in the
 * line, which is exactly the right statement.
 */
const DurationTrendChart: React.FC<DurationTrendChartProps> = ({ months }) => {
  const labels = months.map((m) => monthLabel(m.month));
  const values = months.map((m) =>
    hasMeasurement(m.count) ? m.avgDays : null
  );

  const data = {
    labels,
    datasets: [
      {
        label: "Average days to go live",
        data: values,
        borderColor: COCHARGE_DARK_GREEN,
        backgroundColor: COCHARGE_DARK_GREEN,
        tension: 0.3,
        spanGaps: false,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          // The count is part of the answer: a 12-day average over one prospect
          // is not a trend, and the tooltip is where that becomes visible.
          label: (ctx: any) => {
            const month = months[ctx.dataIndex];
            if (!month || !hasMeasurement(month.count)) return "No data";
            return `${formatDays(month.avgDays)} (${month.count} ${
              month.count === 1 ? "prospect" : "prospects"
            })`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Days" },
      },
    },
  };

  const emptyMonths = months.filter((m) => !hasMeasurement(m.count));

  return (
    <div>
      <div style={{ height: "18rem" }}>
        <Chart type="line" data={data} options={options} />
      </div>
      {emptyMonths.length > 0 && (
        <p className="text-sm text-500 mt-2 mb-0">
          No prospect went live in{" "}
          {emptyMonths.map((m) => monthLabel(m.month)).join(", ")} — those months
          have no data rather than a duration of zero.
        </p>
      )}
    </div>
  );
};

export default DurationTrendChart;
